import { useEffect, useState } from "react";
import { BrandsService } from "../../services/brands.service";
import { BrandsDataType } from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Switch, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { FaPlus } from "react-icons/fa";

const Brands = () => {
  const [brands, setBrands] = useState<BrandsDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<BrandsDataType | null>(
    null
  );

  useEffect(() => {
    setLoading(true);
    BrandsService.getAllBrands()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: BrandsDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setBrands(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await BrandsService.getAllBrands();
    setBrands(
      updatedData?.data?.data?.map((item: BrandsDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const {
    handleSubmit,
    handleChange,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      name: "",
      image: null,
      is_visible: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Brend adı tələb olunur"),
    }),
    onSubmit: async (values) => {
      if (isEditing && editingRecord) {
        const id = editingRecord.id;
        const isEqual =
          editingRecord.name === values.name &&
          editingRecord.image === values.image &&
          editingRecord.is_visible === values.is_visible;
        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          const response = await BrandsService.updateBrand({
            ...values,
            id,
          });
          if (response.status === 200) {
            message.success("Brend uğurla yeniləndi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Brendi yeniləmək alınmadı."
            );
          }
        }
      } else {
        const response = await BrandsService.addBrand(values);
        if (response.status === 200) {
          message.success("Brend uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(response.errorMessage || "Brend əlavə etmək alınmadı.");
        }
      }
      setLoading(false);
    },
  });

  const handleSwitchChange = async (checked: boolean, recordId: number) => {
    BrandsService.updateBrand({
      id: recordId,
      is_visible: checked,
    });
    if (checked) {
      message.success("Brend artıq saytda görünür.");
    } else {
      message.info("Brend artıq saytda görünmür.");
    }
    setBrands((prevData) =>
      prevData.map((item) =>
        item.id === recordId ? { ...item, is_visible: checked } : item
      )
    );
  };

  const handleDeleteClick = async (record: BrandsDataType) => {
    try {
      await BrandsService.deleteBrand(record.id.toString());
      setBrands((prevData) => prevData.filter((item) => item.id !== record.id));
      message.success("Brend uğurla silindi!");
    } catch (error) {
      message.error("Brendi silmək alınmadı.");
    }
  };

  const columns: TableProps<BrandsDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Brend adı",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Saytda görünürlük",
      dataIndex: "is_visible",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.is_visible}
            onChange={(checked) => handleSwitchChange(checked, record.id)}
          />
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu brendi siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
  };
  const handleEditClick = (record: BrandsDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("image", record.image);
    setFieldValue("is_visible", record.is_visible);
  };

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Brendlər</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Brendlər</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<BrandsDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={brands}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Brendi redaktə edin" : "Yeni Brend əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="form_input">
            <label className="label" htmlFor="name">
              Brend adı
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Brend adı"
                value={values.name}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>
          <div className="form_input">
            <label className="active_label" htmlFor="">
              Brendin saytda görünməsi
            </label>
            <Switch
              checked={values.is_visible}
              onChange={(checked) => setFieldValue("is_visible", checked)}
            />
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Brands;
